<template>
  <div class="dss">
    <header
      class="dss__header"
      v-sticky
      sticky-z-index="8"
    >
      <stats-header
        :blood-pressure="bloodPressure"
        :weight="weight"
        :cholesterolLDL="cholesterolLDLInPreferredUnit"
        :cholesterol-l-d-l-unit="cholesterolLDLPreferredUnitLabel"
        :glucose="glucoseInPreferredUnit"
        :glucose-unit="glucosePreferredUnitLabel"
        :knowledge-level-name="knowledgeLevelName"
        :behaviour-goals="behaviourGoals"
        :outcome-goals="outcomeGoals"
        :bmi="BMI"
        :bmi-date="BMIDate"
      >
        <template v-slot:button>
          <v-btn
            outlined
            icon
            color="primary"
            @click="$router.push({ name: 'PatientOverview' })"
          >
            <v-icon :color="$color.coro.purple">mdi-close</v-icon>
          </v-btn>
        </template>
      </stats-header>
    </header>
    <div class="dss__content standard-layout">
      <d-s-s-header></d-s-s-header>
        <v-row v-if="showTabs">
          <v-col cols='9'><d-s-s-button-line></d-s-s-button-line></v-col>
          <v-col cols='3'><egfr /></v-col>
        </v-row>
      <i id="scrolldetectmain" v-scrolldetect="{ offset: -10 }" />
      <router-view></router-view>
    </div>
    <v-dialog
      v-model="showCannotLeaveDialog"
      width="600px"
    >
      <div class="dashboard-dialog-window">
        <div class="dashboard-dialog-window__message">
          {{ $ucfirst($t("dss.youCannotLeaveDSSWhenIncomplete")) }}
        </div>
        <div class="dashboard-dialog-window__actions">
          <v-btn
            x-large
            text
            class="ml-4"
            @click="showCannotLeaveDialog = false"
            color="primary"
          >
            {{ $ucfirst($t('ok')) }}
          </v-btn>
        </div>
      </div>
    </v-dialog>
  </div>
</template>

<script>
import { mapGetters } from 'vuex';

import StatsHeader from "../../components/Parts/StatsHeader";
import DSSHeader from '../../components/Parts/DSSHeader';
import DSSButtonLine from './DSSButtonLine';
import egfr from "../../components/Parts/DSS/EGFR.vue";

/**
 * Component DSS.vue
 *
 * @module DSS
 * @date
 * @author
 * @copyright UniWeb bvba 2022
 *
 * @link https://gitlab.uniweb.be/cor/4.04-caregiver-dashboard
 *
 * @coropreventionSW [SW-4.10] app/src/APP_DASHBOARD/templates/views/DSS/DSS.vue
 * @see https://docs.google.com/document/d/1iOpHH151s_ABr-DniJIc__q-GHpXJJVq/edit#heading=h.a0z8d9xd3xr7
 *
 */

export default {
  name: "DSS",
  components: {
    StatsHeader,
    DSSHeader,
    egfr,
    DSSButtonLine,
  },
  data: function () {
    return {
      showCannotLeaveDialog: false,
    };
  },
  created() {
    this.$app.eventBus.$on('DSS.setCannotLeaveDialog', this.setCannotLeaveDialog)
  },
  beforeDestroy(){
    this.$app.eventBus.$off('DSS.setCannotLeaveDialog', this.setCannotLeaveDialog)
    window.removeEventListener('beforeunload', this.doBeforeRefresh);
  },
  methods: {
    setCannotLeaveDialog(bool){
      this.showCannotLeaveDialog = !this.hasReadOnlyAccess ? bool : false;
    },
    doBeforeRefresh() {
    // before refreshing keep DSSLastAuditTrails saved
      const DSSLastAuditTrails = this.$store.getters['dss/DSSLastAuditTrails'];
      const DSSFirstLoad = this.$store.getters['dss/DSSFirstLoad'];
      localStorage.setItem('DSSLastAuditTrails', JSON.stringify(DSSLastAuditTrails));
      localStorage.setItem('DSSFirstLoad', JSON.stringify({...DSSFirstLoad, patientId: this.patientId}));
    }
  },
  computed: {
    ...mapGetters({
      gender: 'patientSummary/gender',
      age: 'patientSummary/age',
      heartRate: 'patientSummary/heartRate',
      distanceWalked: 'patientSummary/distanceWalked',
      bloodPressure: 'patientSummary/bloodPressure',
      weight: 'patientSummary/weight',
      cholesterolLDLInPreferredUnit: 'patientSummary/cholesterolLDLInPreferredUnit',
      cholesterolLDLPreferredUnitLabel: 'patientSummary/cholesterolLDLPreferredUnitLabel',
      glucoseInPreferredUnit: 'patientSummary/glucoseInPreferredUnit',
      glucosePreferredUnitLabel: 'patientSummary/glucosePreferredUnitLabel',
      knowledgeLevelName: 'patientSummary/knowledgeLevelName',
      behaviourGoals: 'patientSummary/behaviourGoals',
      outcomeGoals: 'patientSummary/outcomeGoals',
      BMI: 'patientSummary/BMI',
      BMIDate: 'patientSummary/BMIDate',
      hasReadOnlyAccess: 'user/hasReadOnlyAccess',
      patientId: 'patientSummary/id'
    }),
    showTabs() {
      return this.$route.name !== "DSSMedicationPrescription"
    }
  },
  mounted() {
    this.$store.dispatch('dss/setPrescriptionRecentlyUpdatedWarning')
    window.addEventListener('beforeunload', this.doBeforeRefresh);
  },
  beforeRouteLeave(to, from, next) {
    // Check if unHandled Medication Recommendations. -> No leave without fixing everything.

    // Except Logout
    if (to.name === "Login") {
      next()
      return;
    }

    if (this.$store.getters['dss/unhandledMedicationRecommendations'].length && !this.hasReadOnlyAccess) {
      this.showCannotLeaveDialog = true;

      next({ name: "DSSCardioMedication" });
      // alert(this.$ucfirst(this.$t("dss.youCannotLeaveDSSWhenIncomplete")));
    } else {
      next();
    }

    next();
  },
  beforeRouteUpdate(to, from, next) {
    // checking route changes inside DSS
    if (to.name === 'DSSMedicationPrescription') {
      if (this.$store.getters['dss/unhandledMedicationRecommendations'].length && !this.hasReadOnlyAccess) {
        this.showCannotLeaveDialog = true;
        if (from.name === 'DSSCardioMedication') {
          return false;
        }
        next({ name: "DSSCardioMedication" });
      } else {
        next()
      }
    } else {
      next();
    }
  },
};
</script>

<style lang="scss">
.dss__header {
  background-color: #fff ;
}

.dss__subheader {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.dss__subheader-main {
  display: flex;
  align-items: center;
}
</style>
